import React, { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import styles from './CheckoutPage.module.css';
import { PaymentMethodService } from 'Logic/PaymentMethodService';
import type { IPaymentMethodView } from '@model/PaymentMethod.model';
import { UserAuthenticationStoreImplementation } from 'Data/UserAuthentication/UserAuthenticationStoreImplementation';
import { useForceLogoutAndRedirect } from 'Logic/Functions/Hooks/useForceLogoutAndRedirect';
import { UnauthorizedPopupStoreImplementation } from 'Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation';
import { useNavigate } from 'react-router-dom';
import useImageUploadRules from 'Logic/Functions/Hooks/useImageUploadRules';
import { OrderStoreImplementation } from 'Data/Order/OrderStoreImplementation';

const CheckoutPaymentSection: FC = () => {
  const navigate = useNavigate();
  const service = new PaymentMethodService();
  const { authenticationState } = UserAuthenticationStoreImplementation();
  const authToken = authenticationState?.token?.trim() ?? '';
  const onAuthError = useForceLogoutAndRedirect();
  const { showPopup } = UnauthorizedPopupStoreImplementation();

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [items, setItems] = useState<IPaymentMethodView[]>([]);
  const { selectedPaymentMethod, setSelectedPaymentMethod } = OrderStoreImplementation();

  const { buildImageSrc: buildIconSrc } = useImageUploadRules({
    mode: 'image',
    maxFiles: 1,
    maxSizeMB: 5,
    requireAtLeastOne: false,
    enforceTotalSize: false,
  });

  useEffect(() => {
    service.handleGetPaymentMethods({
      setLoading,
      setApiError,
      setItems,
      token: authToken,
      onAuthError,
      onForbidden: (msg: string) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
    });
  }, [authToken]);

  useEffect(() => {
    service.computeSelectedMethod({
      availableMethods: items,
      selectedPaymentMethod,
      setSelectedPaymentMethod,
    });
  }, [items, selectedPaymentMethod, setSelectedPaymentMethod]);

  const handleSelect = useCallback(
    (method: IPaymentMethodView) => {
      setSelectedPaymentMethod(method)
    },
    [setSelectedPaymentMethod]
  );

  return (
    <section className={styles.cashSection} data-testid="checkout-payment-section">
      <header className={styles.cashHeader} data-testid="checkout-payment-header">
        <h3 className={styles.cashTitle} data-testid="checkout-payment-title">
          Payment
        </h3>
        <p className={styles.cashSubtitle} data-testid="checkout-payment-subtitle">
          All transactions are secure and encrypted.
        </p>
      </header>

      {apiError && (
        <div className={styles.error} data-testid="checkout-payment-error">
          {apiError}
        </div>
      )}

      <div className={styles.cashCard} data-testid="checkout-payment-card">
        {loading && (
          <div className={styles.placeholder} data-testid="checkout-payment-loading">
            Loading payment methods...
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className={styles.empty} data-testid="checkout-payment-empty">
            No payment methods available.
          </div>
        )}

        {!loading && items.length > 0 && (
          <Fragment>
            <div className={styles.cashCardTop} data-testid="checkout-payment-methods-top">
              <span className={styles.cashMethodTitle} data-testid="checkout-payment-methods-title">
                Choose payment method
              </span>
            </div>

            <div className={styles.cashDivider} data-testid="checkout-payment-divider" />

            <div className={styles.cashCardBody} data-testid="checkout-payment-methods-body">
              <ul className={styles['payment-list']} data-testid="checkout-payment-methods-list">
                {items.map((paymentMethod) => (
                  <li key={paymentMethod.id} className={styles['payment-listItem']}>
                    <button
                      type="button"
                      className={`${styles['payment-methodRow']} ${
                        paymentMethod.id === selectedPaymentMethod?.id
                          ? styles['payment-methodRowActive']
                          : ''
                      }`}
                      onClick={() => handleSelect(paymentMethod)}
                      data-testid={`checkout-payment-method-${paymentMethod.id}`}
                      aria-pressed={paymentMethod.id === selectedPaymentMethod?.id}
                    >
                      <span className={styles['payment-radio']} aria-hidden="true">
                        <span
                          className={`${styles['payment-dot']} ${
                            paymentMethod.id === selectedPaymentMethod?.id
                              ? styles['payment-dotOn']
                              : ''
                          }`}
                        />
                      </span>

                      {buildIconSrc(paymentMethod.icon) && (
                        <img
                          src={buildIconSrc(paymentMethod.icon)!}
                          alt=""
                          aria-hidden="true"
                          className={styles['payment-methodIcon']}
                        />
                      )}

                      <span className={styles['payment-methodText']}>
                        <span className={styles['payment-methodLabel']}>{paymentMethod.label}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              {selectedPaymentMethod?.name === 'CASH_ON_DELIVERY' && (
                <div className={styles['payment-cashInfo']} data-testid="checkout-payment-cod-info">
                  <p className={styles.cashQuestion}>
                    Can a customer refuse to receive and pay for a Cash on Delivery (COD) order?
                  </p>
                  <p className={styles.cashAnswer}>
                    Yes—however, refusal is accepted <strong>only</strong> if the delivered items
                    are <strong>incorrect</strong> or <strong>damaged</strong>.
                    <br />
                    <br />
                    If this happens, please inform the delivery rider of the reason for refusal so
                    the order can be returned to us promptly for verification and resolution.
                  </p>
                </div>
              )}
            </div>
          </Fragment>
        )}
      </div>
    </section>
  );
};

export default CheckoutPaymentSection;
